<script lang="ts">
export default {
  name: 'UIButton',
  inheritAttrs: false,
};

const buttonVariants = cva(
  'tw:btn tw:inline-flex tw:items-center tw:justify-center tw:gap-2 tw:whitespace-nowrap tw:rounded-md tw:text-sm tw:font-medium tw:ring-offset-background tw:transition-colors tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-offset-2 tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:[&_svg]:pointer-events-none tw:[&_svg]:size-4 tw:[&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'tw:bg-secondary tw:text-white tw:hover:bg-secondary/90',
        destructive: 'tw:bg-destructive tw:text-destructive-foreground tw:hover:bg-destructive/90',
        outline: 'tw:border tw:border-input tw:hover:bg-secondary/80',
        secondary: 'tw:bg-secondary tw:hover:bg-secondary/80 tw:text-white',
        ghost: 'tw:hover:bg-secondary tw:hover:text-accent-foreground',
        link: 'tw:text-primary tw:underline-offset-4 tw:hover:underline',
      },
      size: {
        default: '',
        sm: 'tw:btn-sm',
        lg: 'tw:btn-lg',
        icon: 'tw:h-10 tw:w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonVariants = VariantProps<typeof buttonVariants>;
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

interface Props extends /* @vue-ignore */ ButtonVariants {
  as?: string;
  class?: string;
  type?: 'button' | 'submit' | 'reset';
  variant?: ButtonVariants['variant'];
  size?: ButtonVariants['size'];
}

const props = withDefaults(defineProps<Props>(), {
  as: 'button',
  type: 'button',
});

const buttonClass = computed(() => cn(buttonVariants({ variant: props.variant, size: props.size }), props.class));
</script>

<template>
  <component :is="as" :class="buttonClass" :type="type" v-bind="$attrs">
    <slot />
  </component>
</template>
